import { Badge, Card, Checkbox, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import type { InterestedSib } from '../types'

interface InterestedSibCardProps {
  sib: InterestedSib
  checked: boolean
  onChange: (checked: boolean) => void
}

function CapacityValue({
  value,
  label,
  warning,
}: {
  value: string
  label: string
  warning?: boolean
}) {
  return (
    <Stack gap={1}>
      <HStack>
        <Text fontWeight="semibold">{value}</Text>
        {warning && (
          <Badge colorPalette="red" variant="subtle">
            !
          </Badge>
        )}
      </HStack>
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
    </Stack>
  )
}

function getRemainingUnits(sib: InterestedSib, serviceType: string) {
  return sib.remainingCapacity?.find(item => item.serviceType === serviceType)?.units ?? 0
}

function getOrderedUnits(sib: InterestedSib, serviceType: string) {
  const item = sib.orderedUnits?.find(item => item.serviceType === serviceType)

  return {
    ordered: item?.ordered ?? 0,
    completed: item?.completed ?? 0,
  }
}

function formatTargetAchievement(value?: number) {
  if (value == null) return null
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function InterestedSibCard({ sib, checked, onChange }: InterestedSibCardProps) {
  return (
    <Card.Root
      borderColor={checked ? 'blue.500' : 'border.muted'}
      cursor="pointer"
      onClick={() => onChange(!checked)}
    >
      <Card.Body p={6}>
        <Stack gap={5}>
          <HStack justify="space-between" align="start">
            <HStack>
              <Checkbox.Root
                checked={checked}
                onClick={event => event.stopPropagation()}
                onCheckedChange={details => onChange(Boolean(details.checked))}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>

              <Text fontWeight="bold">{sib.name}</Text>
              {formatTargetAchievement(sib.targetAchievement) && (
                <Badge colorPalette="gray">
                  Zeitziel {formatTargetAchievement(sib.targetAchievement)}
                </Badge>
              )}
            </HStack>

            <Badge colorPalette="green">
              {sib.postalCode || '—'} {sib.city || ''},{' '}
              {sib.workingArea ? `, ${sib.workingArea}` : ''}
            </Badge>
          </HStack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Restkapazität
            </Text>

            <SimpleGrid columns={4}>
              <CapacityValue value={`${getRemainingUnits(sib, 'TOJ')} Stk.`} label="TOJ" />
              <CapacityValue
                value={`${getRemainingUnits(sib, 'CONSULTING')} St.`}
                label="Beratung"
              />
              <CapacityValue
                value={`${getRemainingUnits(sib, 'TRAINING')} T.`}
                label="Training"
                warning={getRemainingUnits(sib, 'TRAINING') <= 0}
              />
              <CapacityValue
                value={`${getRemainingUnits(sib, 'PILOT')} Stk.`}
                label="Pilot"
                warning={getRemainingUnits(sib, 'PILOT') <= 0}
              />
            </SimpleGrid>
          </Stack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Bestellt / Durchgeführt
            </Text>

            <SimpleGrid columns={4}>
              {(['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'] as const).map(serviceType => {
                const units = getOrderedUnits(sib, serviceType)

                return (
                  <CapacityValue
                    key={serviceType}
                    value={`${units.ordered} / ${units.completed}`}
                    label={
                      serviceType === 'TOJ'
                        ? 'TOJ'
                        : serviceType === 'CONSULTING'
                          ? 'Beratung'
                          : serviceType === 'TRAINING'
                            ? 'Training'
                            : 'Pilot'
                    }
                  />
                )
              })}
            </SimpleGrid>
          </Stack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Themen
            </Text>

            <HStack wrap="wrap">
              {sib.topics && sib.topics.length > 0 ? (
                sib.topics.map(topic => (
                  <Badge key={topic} colorPalette="green">
                    {topic}
                  </Badge>
                ))
              ) : (
                <Text color="fg.muted">—</Text>
              )}
            </HStack>
          </Stack>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
